import { EntityAction, createConfirmDialog } from 'components';
import type { Ticket } from 'modules/support/types';
import { sessionUser, checkPerm } from 'core/permissions';
import companyPerms from 'modules/core/company/permissions';
import vendorPerms from 'modules/supplier/vendor/permissions';
import serviceCategoryPerms from 'modules/supplier/outletOrServiceCategory/serviceCategoryPermissions';
import clientPerms from 'modules/client/permissions';
import agreementPerms from 'modules/client/agreement/permissions';

const dialog = createConfirmDialog();

const actions: EntityAction<Ticket>[] = [
  ({
    item: {
      id,
      assignee,
      enterprise: { slug },
    },
  }) => ({
    action: 'create_company',
    permKey: (sess) => {
      // assinge or superuser and can add company
      const u = sessionUser(sess);
      return (u?.is_superuser || u?.id === assignee?.id) && checkPerm(sess, companyPerms.add);
    },
    button: {
      children: 'Create company',
      variant: 'primary',
      to: `/${slug}/preferences/companies/from_ticket/${id}`,
      target: '_blank',
    },
    visible: ({ state, category }) => state === 'OPEN' && category === 'ADD_COMPANY',
  }),

  ({
    item: {
      assignee,
      enterprise: { slug },
    },
  }) => ({
    action: 'create_vendor',
    permKey: (sess) => {
      // assinge or superuser and can view agreement
      const u = sessionUser(sess);
      return (u?.is_superuser || u?.id === assignee?.id) && checkPerm(sess, vendorPerms.view);
    },
    button: {
      children: 'Vendors',
      variant: 'primary',
      to: `/${slug}/preferences/vendors`,
      target: '_blank',
    },
    visible: ({ state, category }) => state === 'OPEN' && category === 'ADD_OUTLET_OR_SERVICE_CATEGORY',
  }),
  ({
    item: {
      assignee,
      enterprise: { slug },
    },
  }) => ({
    action: 'create_service_category',
    permKey: (sess) => {
      // assinge or superuser and can view agreement
      const u = sessionUser(sess);
      return (u?.is_superuser || u?.id === assignee?.id) && checkPerm(sess, serviceCategoryPerms.view);
    },
    button: {
      children: 'Service categories',
      variant: 'primary',
      to: `/${slug}/preferences/service_categories`,
      target: '_blank',
    },
    visible: ({ state, category }) => state === 'OPEN' && category === 'ADD_OUTLET_OR_SERVICE_CATEGORY',
  }),
  ({
    item: {
      id,
      assignee,
      enterprise: { slug },
      data,
    },
  }) => ({
    action: 'create_client_agreement',
    permKey: (sess) => {
      // assinge or superuser and can view client and add agreement
      const u = sessionUser(sess);
      return (
        (u?.is_superuser || u?.id === assignee?.id) &&
        checkPerm(sess, clientPerms.view) &&
        checkPerm(sess, agreementPerms.add)
      );
    },
    button: {
      children: 'Create agreement',
      variant: 'primary',
      to: `/${slug}/contacts/clients/${data?.company?.id}/agreements/from_ticket/${id}`,
      target: '_blank',
    },
    visible: ({ state, category }) => state === 'OPEN' && category === 'ADD_CLIENT',
  }),
  ({ item: { assignee } }) => ({
    action: 'close',
    permKey: (sess) => {
      // assinge or superuser
      const u = sessionUser(sess);
      return u?.is_superuser || u?.id === assignee?.id;
    },
    dialog,
    button: {
      children: 'Close ticket',
      variant: 'secondary',
    },
    visible: ({ state }) => state === 'OPEN',
  }),
  ({ item: { author } }) => ({
    action: 'cancel',
    permKey: (sess) => {
      // author or superuser
      const u = sessionUser(sess);
      return u?.is_superuser || u?.id === author?.id;
    },
    dialog,
    button: {
      children: 'Cancel ticket',
      variant: 'secondary-danger',
    },
    visible: ({ state }) => state === 'OPEN',
  }),
];

export default actions;
